import { createContext, useContext, useState, type ReactNode } from 'react'
import type { CardContext } from '@/types/cardContext'

export type IntentKind = 'capture_memory' | 'open_upgrade' | 'open_settings' | 'open_mori_chat'

export interface DeferredIntent {
  kind: IntentKind
  payload?: {
    contentLibraryId?: string
    promptText?: string
    /**
     * Full card context the intent originated from. Captured so the
     * sheet-close auto-resume can re-open capture with the same prompt /
     * captureType / title the card surfaced — falling back to
     * contentLibraryId-only when the intent didn't originate from a card.
     */
    cardContext?: CardContext
  }
}

interface DeferredIntentContextValue {
  current: DeferredIntent | null
  setIntent(intent: DeferredIntent): void
  clear(): void
  fire(): DeferredIntent | null
  isCurrent(contentLibraryId: string): boolean
}

const DeferredIntentContext = createContext<DeferredIntentContextValue>({
  current: null,
  setIntent: () => {},
  clear: () => {},
  fire: () => null,
  isCurrent: () => false,
})

export function DeferredIntentProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<DeferredIntent | null>(null)

  const setIntent = (intent: DeferredIntent) => setCurrent(intent)

  const clear = () => setCurrent(null)

  const fire = (): DeferredIntent | null => {
    const saved = current
    setCurrent(null)
    return saved
  }

  const isCurrent = (contentLibraryId: string): boolean =>
    current?.payload?.contentLibraryId === contentLibraryId

  return (
    <DeferredIntentContext.Provider value={{ current, setIntent, clear, fire, isCurrent }}>
      {children}
    </DeferredIntentContext.Provider>
  )
}

export function useDeferredIntent() {
  return useContext(DeferredIntentContext)
}
